import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import ReorderIcon from '@mui/icons-material/Reorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import BallotIcon from '@mui/icons-material/Ballot';
import { useTranslation } from 'react-i18next';
import JSONPretty from 'react-json-pretty';
import { Dialog, IconButton } from '@totalsoft/rocket-ui';
import { RightsContext } from 'providers/RightsContext';

const getDifferences = (a, b) => Object.entries(a).reduce((ac, [k, v]) => (b[k] && b[k] !== v ? ((ac[k] = b[k]), ac) : ac), {});
const areEqual = (a, b) => {
  var aProps = Object.getOwnPropertyNames(a);
  var bProps = Object.getOwnPropertyNames(b);

  if (aProps.length !== bProps.length) {
    return false;
  }

  for (var i = 0; i < aProps.length; i++) {
    var propName = aProps[i];

    if (a[propName] !== b[propName]) {
      return false;
    }
  }
  return true;
};

const GroupContainerBase = ({
  getComponent,
  getComponentExtraParams,
  reorderComponentsName,
  defaultComponentsOrder,
  userComponentsOrder,
  reorderEnabled,
  setReorderEnabled,
  onSave,
  isSaving,
  disabled
}) => {
  const { t } = useTranslation();

  const [localComponentsOrder, setLocalComponentsOrder] = useState({});
  const [localComponentsOrderSortedList, setLocalComponentsOrderSortedList] = useState([]);
  const [showModificationsModal, setShowModificationsModal] = useState(false);
  const { documentReorderComponents } = useContext(RightsContext);

  useEffect(() => {
    setLocalComponentsOrder({ ...defaultComponentsOrder, ...userComponentsOrder });
  }, [defaultComponentsOrder, userComponentsOrder]);

  useEffect(() => {
    setLocalComponentsOrderSortedList(
      Object.keys(localComponentsOrder).sort(function (a, b) {
        return localComponentsOrder[a] - localComponentsOrder[b];
      })
    );
  }, [localComponentsOrder]);

  const swapUp = index => {
    let currentComponent = localComponentsOrderSortedList[index + 1];
    let prevComponent = localComponentsOrderSortedList[index];

    let newLocalComponentsOrder = {
      ...localComponentsOrder,
      [currentComponent]: localComponentsOrder[prevComponent],
      [prevComponent]: localComponentsOrder[currentComponent]
    };
    setLocalComponentsOrder({ ...newLocalComponentsOrder });
  };

  const swapDown = index => {
    let currentComponent = localComponentsOrderSortedList[index];
    let nextComponent = localComponentsOrderSortedList[index + 1];

    let newLocalComponentsOrder = {
      ...localComponentsOrder,
      [currentComponent]: localComponentsOrder[nextComponent],
      [nextComponent]: localComponentsOrder[currentComponent]
    };
    setLocalComponentsOrder({ ...newLocalComponentsOrder });
  };

  const saveReorders = () => {
    const newUserOrders = getDifferences(defaultComponentsOrder, localComponentsOrder);
    const ok = areEqual(newUserOrders, userComponentsOrder);
    if (ok) {
      setReorderEnabled(false);
      return;
    }

    onSave(newUserOrders);
  };

  const updateShowModificationsModal = useCallback(value => () => setShowModificationsModal(value), []);

  const getComponentLocal = (index, key) => {
    const component = getComponent(key, reorderEnabled, getComponentExtraParams);
    if (component) {
      return (
        <div key={index}>
          {component}
          {reorderEnabled && index !== localComponentsOrderSortedList.length - 1 && (
            <Grid container justifyContent='center'>
              <IconButton
                id={'swapDown_' + index}
                color='primary'
                variant='text'
                size='small'
                onClick={() => {
                  swapDown(index);
                }}
              >
                <ArrowDownwardIcon />
              </IconButton>
              <IconButton
                id={'swapUp'}
                color='primary'
                variant='text'
                size='small'
                onClick={() => {
                  swapUp(index);
                }}
              >
                <ArrowUpwardIcon />
              </IconButton>
            </Grid>
          )}
        </div>
      );
    }
  };

  return (
    <>
      {localComponentsOrderSortedList.map((key, index) => {
        return getComponentLocal(index, key);
      })}
      {documentReorderComponents && !reorderEnabled && (
        <Grid container justifyContent='end'>
          <IconButton
            color='primary'
            variant='text'
            size='small'
            tooltip={t('GroupContainer.Reorder')}
            id={'Reorder'}
            onClick={() => {
              setReorderEnabled(true);
            }}
          >
            <ReorderIcon />
          </IconButton>
        </Grid>
      )}
      {documentReorderComponents && reorderEnabled && (
        <Grid container justifyContent='end' gap={1}>
          <IconButton
            onClick={updateShowModificationsModal(true)}
            id={`reorder_showJsonObject`}
            color='primary'
            variant='text'
            size='small'
            tooltip={t('GroupContainer.ShowModifications')}
            disabled={isSaving}
          >
            <BallotIcon />
          </IconButton>
          <IconButton
            onClick={saveReorders}
            id={`reorder_saveBtn`}
            color='primary'
            variant='text'
            size='small'
            tooltip={t('GroupContainer.Save')}
            disabled={isSaving || disabled}
          >
            <SaveIcon />
          </IconButton>
        </Grid>
      )}
      <Dialog
        open={showModificationsModal}
        fullWidth
        maxWidth='lg'
        onClose={updateShowModificationsModal(false)}
        content={
          <Grid container direction='row' justifyContent='center' alignItems='flex-start'>
            <Grid item xs>
              {t('GroupContainer.Standard')} <JSONPretty data={defaultComponentsOrder} />
            </Grid>
            <Grid item xs>
              {t('GroupContainer.UserModifications')} ({reorderComponentsName})
              <JSONPretty data={getDifferences(defaultComponentsOrder, localComponentsOrder)} />
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

GroupContainerBase.propTypes = {
  reorderComponentsName: PropTypes.string,
  reorderEnabled: PropTypes.bool,
  setReorderEnabled: PropTypes.func,
  defaultComponentsOrder: PropTypes.object,
  userComponentsOrder: PropTypes.object,
  getComponent: PropTypes.func,
  getComponentExtraParams: PropTypes.any,
  onSave: PropTypes.func,
  isSaving: PropTypes.bool,
  disabled: PropTypes.bool
};

export default GroupContainerBase;
